import React from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useRecoilCallback, useSetRecoilState } from 'recoil'
import styled from '@emotion/styled'

import {
  Button,
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_SECONDARY,
  IconFAAngleDoubleDown,
  IconFAAngleDoubleUp,
  IconInfo,
  Flex,
  FlexItem,
  Text,
  TEXT_VARIANT_BOLD_LIGHT,
  TEXT_VARIANT_SMALL_LIGHT,
  Collapsible,
  SpacingVertical,
  SPACING_8,
  Link,
  LINK_VARIANT_EXTRALIGHT
} from 'mailup-web-components'

import ArrowBaloon from './ArrowBaloon'

import {
  openCampaignModaleCallback,
  sendingSegmentationCallback,
  toggleExpandCallback,
  openRescheduleModaleCallback
} from 'Pages/ScheduledSendings/recoil/callbacks/callbacks'
import { modalStopSendingState } from 'Pages/ScheduledSendings/recoil/state/modals'
import { useHistory } from 'react-router-dom'

const StyledSendingItemContainer = styled.div`
  padding-right: 35px;
  padding-bottom: 20px;
  position: relative;

  &:after {
    position: absolute;
    content: '';
    background-color: rgb(230, 228, 228);
    width: 4px;
    top: 0;
    right: 6px;

    ${({ isLast }) =>
      `${isLast ? 'bottom: initial; height: 18px;' : 'bottom: 0;'}`}
  }

  .e-collapsible--expanded {
    max-height: 220px !important;
  }
`

const StyledSendingItemBox = styled.div`
  position: relative;
  box-sizing: border-box;

  border: 1px solid var(--geyser);
  border-radius: 6px;

  padding: 10px 15px 10px;

  cursor: pointer;

  background-color: white;
  color: var(--boulder);

  box-shadow: none;
  transition: box-shadow 0.25s ease-in-out;

  &:hover {
    box-shadow: 0px 2px 3px 0 #ccc;
  }
`

const StyledSendingItemTrigger = styled.span`
  color: var(--java);
  cursor: pointer;
  font-size: 12px;
`

const StyledSendingItemTime = styled.span`
  font-family: 'Roboto Condensed', arial, sans-serif;
  font-weight: 500;
  color: #39ba7c;
`

const StyledBaloon = styled.div`
  position: absolute;
  right: -8px;
  top: 10px;
  width: 8px;
  height: 13px;
`

const backgroundColors = [
  '#8ACF61',
  '#4CBAD7',
  '#FFC31A',
  '#9C74DF',
  '#FF99AF',
  '#24CDC3',
  '#A0A0A0',
  '#FF9000',
  '#FE6A6A',
  '#71CFA0',
  '#14839F'
]
const borderColors = [
  '#C5E7B0',
  '#BBE1EB',
  '#F8E7B5',
  '#D7C9ED',
  '#F8D6DE',
  '#B8E8E5',
  '#D8D8D8',
  '#F8CF9B',
  '#F7C1C1',
  '#BEE6D2',
  '#B2D2DA'
]

const StyledBullet = styled.span`
  position: absolute;
  right: 0;
  top: 10px;
  display: block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border-style: solid;
  border-width: 3px;
  z-index: 2;
  ${({ variant }) => `
    background-color: ${backgroundColors[variant]};
    border-color: ${borderColors[variant]};
  `}
`

const TimelineSendingItem = ({
  id,
  idMessage,
  idList,
  idABTest,
  title,
  scheduledFor,
  timezoneFormatted,
  timeFormatted,
  recipientsQueued,
  isPartial,
  actions,
  expanded,
  listIndex,
  isLast
}) => {
  const intl = useIntl()
  const history = useHistory()

  const toggleExpandCollapse = useRecoilCallback(toggleExpandCallback, [])
  const setModalStopEditing = useSetRecoilState(modalStopSendingState)
  const segmentation = useRecoilCallback(sendingSegmentationCallback, [])
  const stopCampaignScheduled = useRecoilCallback(
    openCampaignModaleCallback,
    []
  )

  const openRescheduleModale = useRecoilCallback(
    openRescheduleModaleCallback,
    []
  )
  const handleToggleExpandCollapse = () => {
    toggleExpandCollapse(id)
  }

  const handleSelectedRecipientsClick = e => {
    e.stopPropagation()
    segmentation(id)
  }

  const handleReschedule = e => {
    e.stopPropagation()
    openRescheduleModale(id)
  }

  const handleManageABTest = e => {
    e.stopPropagation()
    history.push(`/messages/email/abtestmanage/${idABTest}`)
  }

  const handleStop = e => {
    e.stopPropagation()
    setModalStopEditing(prev => ({
      ...prev,
      show: true,
      idSending: id // idTimeLine
    }))
  }

  const handleStopCampaign = e => {
    e.stopPropagation()
    stopCampaignScheduled(id)
  }

  return (
    <StyledSendingItemContainer
      isLast={isLast}
      data-mu-test='timeline-sending-item'
    >
      <StyledBullet variant={parseInt(listIndex % 10)} />
      <StyledSendingItemBox onClick={handleToggleExpandCollapse}>
        <StyledBaloon>
          <ArrowBaloon />
        </StyledBaloon>

        <Flex alignItems='flex-start' justifyContent='space-between'>
          <StyledSendingItemTrigger>
            {expanded ? <IconFAAngleDoubleUp /> : <IconFAAngleDoubleDown />}
          </StyledSendingItemTrigger>
          <StyledSendingItemTime>{timeFormatted}</StyledSendingItemTime>
        </Flex>
        <Text variant={TEXT_VARIANT_BOLD_LIGHT}>{title}</Text>
        <Flex justifyContent='space-between' margin='4px 0 8px 0'>
          <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
            {intl.formatMessage({ id: 'Load.MailupCommon.Id' }).toUpperCase()}{' '}
            {idMessage}
          </Text>
          <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
            {intl.formatMessage({ id: 'Load.MailupCommon.List' }).toUpperCase()}{' '}
            {idList}
          </Text>
        </Flex>

        <Collapsible collapsed={!expanded}>
          <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
            {intl.formatMessage({ id: 'Load.ScheduledMailing.Scheduled' })}:{' '}
            {scheduledFor}
          </Text>
          <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
            {intl.formatMessage({
              id: 'Load.MailupCommon.setScheduleLblFuso'
            })}
            : {timezoneFormatted}
          </Text>
          <Text variant={TEXT_VARIANT_SMALL_LIGHT} htmlElement='span'>
            {intl.formatMessage({ id: 'Load.MailupCommon.Recipients' })}:{' '}
            {intl.formatNumber(recipientsQueued)}{' '}
          </Text>

          <Text htmlElement='span' variant={TEXT_VARIANT_SMALL_LIGHT}>
            {isPartial ? (
              <>
                {intl.formatMessage({
                  id: 'Load.Schedulation.StatusTotPartialRecipients'
                })}
                <span
                  data-tip={intl.formatMessage({
                    id: 'Load.Schedulation.PartialSendingTooltip'
                  })}
                >
                  {' '}
                  <IconInfo />
                </span>
              </>
            ) : (
              intl.formatMessage({
                id: 'Load.Schedulation.StatusTotRecipients'
              })
            )}
          </Text>

          <SpacingVertical variant={SPACING_8} />
          {actions.segmentation && (
            <>
              <Link
                variant={LINK_VARIANT_EXTRALIGHT}
                onClick={handleSelectedRecipientsClick}
              >
                {intl.formatMessage({
                  id: 'Load.Schedulation.SelectedRecipients'
                })}
              </Link>
              <SpacingVertical variant={SPACING_8} />
            </>
          )}

          <Flex flexWrap='wrap' rowGap='8px' columnGap='8px'>
            {actions.reschedule && (
              <FlexItem>
                <Button
                  kind={BUTTON_VARIANT_PRIMARY}
                  onClick={handleReschedule}
                >
                  {intl.formatMessage({
                    id: 'Load.Schedulation.ActionRecover'
                  })}
                </Button>
              </FlexItem>
            )}
            {actions.manageABTest && (
              <FlexItem>
                <Button
                  kind={BUTTON_VARIANT_PRIMARY}
                  onClick={handleManageABTest}
                >
                  {intl.formatMessage({
                    id: 'Load.Schedulation.ActionManageABTest'
                  })}
                </Button>
              </FlexItem>
            )}
            {actions.stop && (
              <FlexItem>
                <Button kind={BUTTON_VARIANT_SECONDARY} onClick={handleStop}>
                  {intl.formatMessage({
                    id: 'Load.Schedulation.ActionStop'
                  })}
                </Button>
              </FlexItem>
            )}
            {actions.stopCampaign && (
              <FlexItem>
                <Button
                  kind={BUTTON_VARIANT_SECONDARY}
                  onClick={handleStopCampaign}
                >
                  {intl.formatMessage({
                    id: 'Load.Schedulation.ActionStop'
                  })}
                </Button>
              </FlexItem>
            )}
          </Flex>
        </Collapsible>
      </StyledSendingItemBox>
    </StyledSendingItemContainer>
  )
}

TimelineSendingItem.propTypes = {
  id: PropTypes.number,
  idMessage: PropTypes.number,
  idList: PropTypes.number,
  idABTest: PropTypes.number,
  title: PropTypes.string,
  scheduledFor: PropTypes.string,
  timezoneFormatted: PropTypes.string,
  timeFormatted: PropTypes.string,
  recipientsQueued: PropTypes.number,
  isPartial: PropTypes.bool,
  actions: PropTypes.shape({
    segmentation: PropTypes.bool,
    reschedule: PropTypes.bool,
    manageABTest: PropTypes.bool,
    stop: PropTypes.bool,
    stopCampaign: PropTypes.bool
  }),
  expanded: PropTypes.bool,
  listIndex: PropTypes.number,
  isLast: PropTypes.bool
}

export default TimelineSendingItem
